import { seaWaybillStrings } from '@data/seaWaybill';
import { formStrings } from '@utils/forms';
import { z } from 'zod';

export const SeaWaybillPrepaidCollect = z.enum(['PREPAID', 'COLLECT']);

export type SeaWaybillPrepaidCollect = z.infer<typeof SeaWaybillPrepaidCollect>;

export const SeaWaybill = z.object({
  booking_inquiry: z.number(),
  id: z.number(),
  house_bl_number: z.string().label(seaWaybillStrings.house_bl_number),
  place_of_receipt: z.string().label(seaWaybillStrings.place_of_receipt),
  shipper: z.string().label(seaWaybillStrings.shipper),
  consignee: z.string().label(seaWaybillStrings.consignee),
  place_of_delivery: z.string().label(seaWaybillStrings.place_of_delivery),
  commodity: z.string().label(seaWaybillStrings.commodity),
  shipping_mark: z.string().label(seaWaybillStrings.shipping_mark),
  prepaid_collect: z.string().label(seaWaybillStrings.prepaid_collect),
  payments_terms: z.string().label(seaWaybillStrings.payments_terms),
  nos: z.number().label(seaWaybillStrings.nos),
  packages: z.string().label(seaWaybillStrings.packages),
  gross_weight: z.string().label(seaWaybillStrings.gross_weight),
  chargeable_weight: z.string().label(seaWaybillStrings.chargeable_weight),
  measurement: z.string().label(seaWaybillStrings.measurement),
  vessel: z.string().label(seaWaybillStrings.vessel),
  voyage: z.string().label(seaWaybillStrings.voyage),
  pol: z.string().label(seaWaybillStrings.pol),
  pod: z.string().label(seaWaybillStrings.pod),
  etd: z.string().label(seaWaybillStrings.etd),
  created_at: z.string(),
  updated_at: z.string(),
});

export type SeaWaybill = z.infer<typeof SeaWaybill>;

export const PostSeaWaybill = z.object({
  booking_inquiry: SeaWaybill.shape.booking_inquiry,
});

export type PostSeaWaybill = z.infer<typeof PostSeaWaybill>;

export const PutSeaWaybill = z.object({
  place_of_receipt: SeaWaybill.shape.place_of_receipt,
  shipper: SeaWaybill.shape.shipper,
  consignee: SeaWaybill.shape.consignee,
  place_of_delivery: SeaWaybill.shape.place_of_delivery,
  commodity: SeaWaybill.shape.commodity,
  shipping_mark: SeaWaybill.shape.shipping_mark,
  prepaid_collect: SeaWaybill.shape.prepaid_collect,
  payments_terms: SeaWaybill.shape.payments_terms,
});

export type PutSeaWaybill = z.infer<typeof PutSeaWaybill>;

export const PatchSeaWaybill = z.object({
  detail: z.string().label(formStrings.changeReason),
  place_of_receipt: SeaWaybill.shape.place_of_receipt.optional(),
  shipper: SeaWaybill.shape.shipper.optional(),
  consignee: SeaWaybill.shape.consignee.optional(),
  place_of_delivery: SeaWaybill.shape.place_of_delivery.optional(),
  commodity: SeaWaybill.shape.commodity.optional(),
  shipping_mark: SeaWaybill.shape.shipping_mark.optional(),
  prepaid_collect: SeaWaybill.shape.prepaid_collect.optional(),
  payments_terms: SeaWaybill.shape.payments_terms.optional(),
});

export type PatchSeaWaybill = z.infer<typeof PatchSeaWaybill>;

export const SeaWaybillForm = z.object({
  action: z.enum(['submit', 'edit']),

  house_bl_number: SeaWaybill.shape.house_bl_number.min(1),
  place_of_receipt: SeaWaybill.shape.place_of_receipt.min(1),
  shipper: SeaWaybill.shape.shipper.min(1),
  consignee: SeaWaybill.shape.consignee.min(1),
  place_of_delivery: SeaWaybill.shape.place_of_delivery.min(1),
  commodity: SeaWaybill.shape.commodity.min(1),
  shipping_mark: SeaWaybill.shape.shipping_mark.min(1),
  prepaid_collect: SeaWaybillPrepaidCollect.label(seaWaybillStrings.prepaid_collect),

  // Display only fields
  payments_terms: SeaWaybill.shape.payments_terms,
  nos: SeaWaybill.shape.nos,
  packages: SeaWaybill.shape.packages,
  gross_weight: SeaWaybill.shape.gross_weight,
  chargeable_weight: SeaWaybill.shape.chargeable_weight,
  measurement: SeaWaybill.shape.measurement,
  vessel: SeaWaybill.shape.vessel,
  voyage: SeaWaybill.shape.voyage,
  pol: SeaWaybill.shape.pol,
  pod: SeaWaybill.shape.pod,
  etd: SeaWaybill.shape.etd,

  // Patch only
  detail: PatchSeaWaybill.shape.detail,
});

export type SeaWaybillForm = z.infer<typeof SeaWaybillForm>;

export class SeaWaybillHelper {
  static isFormValid(seaWaybill: SeaWaybill) {
    return SeaWaybillForm.safeParse(this.mapToForm(seaWaybill)).success;
  }

  static mapToForm(seaWaybill: SeaWaybill): SeaWaybillForm {
    return {
      ...seaWaybill,
      prepaid_collect: seaWaybill.prepaid_collect as SeaWaybillPrepaidCollect,
      action: 'submit',
      detail: '',
    };
  }
}
